/**
 * Field-execution models for משימות שינוע 99 — what one soldier did with one
 * task, as reported from the field.
 *
 * The distinction this layer exists to keep is the one the board depends on: a
 * soldier reporting "סיימתי" is not the task being closed. A soldier owns their
 * own row here — receipt, status updates, execution finished — while the task's
 * lifecycle status stays the manager's, and only a manager writes
 * `Task.closedAt` (see `closeTaskAsManager()` in `src/db/task-store.ts`).
 *
 * One row per (task, soldier) pair, filed in the `taskProgress` table of
 * database version 7. The row is keyed by both ids together, so a soldier's
 * report can never be confused with a crewmate's.
 */
import type { ISODateString } from './index';
import type { TaskStatusId } from './task';

// ── The soldier's own state on one task ──────────────────────────────

/**
 * How far one soldier has taken one task. Derived from the row rather than
 * stored on it — a state that is written twice can disagree with itself.
 */
export const SOLDIER_TASK_STATES = ['pending', 'received', 'in_progress', 'finished'] as const;

export type SoldierTaskState = (typeof SOLDIER_TASK_STATES)[number];

export const SOLDIER_TASK_STATE_LABELS: Record<SoldierTaskState, string> = {
  pending: 'ממתין לאישור קבלה',
  received: 'אישר קבלת משימה',
  in_progress: 'בביצוע',
  finished: 'סיים ביצוע',
};

export const SOLDIER_TASK_STATE_TONES: Record<SoldierTaskState, string> = {
  pending: 'border-line bg-canvas text-ink/60',
  received: 'border-brand-300 bg-brand-50 text-brand-700',
  in_progress: 'border-amber-300 bg-amber-50 text-amber-800',
  finished: 'border-emerald-300 bg-emerald-50 text-emerald-700',
};

// ── Reports ──────────────────────────────────────────────────────────

/** One line the soldier sent from the field, in the order it was sent. */
export interface SoldierTaskUpdate {
  id: string;
  note: string;
  /** The lifecycle status the soldier moved the task to, when they moved one. */
  statusId?: TaskStatusId;
  createdAt: ISODateString;
}

/**
 * One soldier's execution record for one task. `finishedAt` is the soldier's
 * report and nothing more: the task is `completed` only once a manager closes
 * it, which is a separate, permissioned act.
 */
export interface SoldierTaskProgress {
  /** `${taskId}:${soldierId}` — the pair is the identity. */
  id: string;
  taskId: string;
  soldierId: string;
  /** The account that reported, when the soldier has one. */
  userId?: string;
  /** "אישור קבלת משימה" — the moment the soldier acknowledged the task. */
  receiptConfirmedAt?: ISODateString;
  /** "סימון סיום ביצוע" — the soldier's own finish, never the task's. */
  finishedAt?: ISODateString;
  finishNote?: string;
  updates: SoldierTaskUpdate[];
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

/** The stable id of one soldier's row on one task. */
export function soldierProgressId(taskId: string, soldierId: string): string {
  return `${taskId}:${soldierId}`;
}

/** Where this soldier stands, read off their row alone. */
export function soldierTaskState(
  progress: SoldierTaskProgress | null | undefined,
): SoldierTaskState {
  if (!progress) return 'pending';
  if (progress.finishedAt) return 'finished';
  if (progress.updates.length > 0) return 'in_progress';
  if (progress.receiptConfirmedAt) return 'received';
  return 'pending';
}

/** True once every crew member on the task has reported their own finish. */
export function allSoldiersFinished(
  soldierIds: readonly string[],
  progress: readonly SoldierTaskProgress[],
): boolean {
  if (soldierIds.length === 0) return false;
  return soldierIds.every((soldierId) =>
    progress.some((row) => row.soldierId === soldierId && Boolean(row.finishedAt)),
  );
}
